import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Upload, FileText, Briefcase, CheckCircle2, ArrowRight, Loader2, AlertCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AnalyzePage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [resumeFile, setResumeFile] = useState(null);
  const [jobDescription, setJobDescription] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    fetchUsage();
  }, []);

  const fetchUsage = async () => {
    try {
      const response = await axios.get(`${API}/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsage(response.data);
    } catch (error) {
      console.error('Failed to fetch usage:', error);
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragActive(true);
    } else if (e.type === "dragleave") {
      setIsDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  }, []);

  const handleFileSelect = (file) => {
    const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
    const validExtensions = ['.pdf', '.docx', '.txt'];
    
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
      toast.error("Invalid file type. Please upload PDF, DOCX, or TXT files.");
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 10MB.");
      return;
    }
    
    setResumeFile(file);
    toast.success(`Resume uploaded: ${file.name}`);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleAnalyze = async () => {
    if (!resumeFile) {
      toast.error("Please upload your resume");
      return;
    }
    
    if (!jobDescription.trim() || jobDescription.trim().length < 20) {
      toast.error("Please provide a detailed job description (at least 20 characters)");
      return;
    }

    // Check usage limits
    if (usage && usage.analyses_used >= usage.analyses_limit) {
      toast.error("You've reached your analysis limit. Please upgrade to continue.");
      navigate('/pricing');
      return;
    }

    setIsAnalyzing(true);
    
    try {
      const formData = new FormData();
      formData.append('resume', resumeFile);
      formData.append('job_description', jobDescription);

      const response = await axios.post(`${API}/analyze`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
        timeout: 120000,
      });

      toast.success("Analysis complete!");
      navigate(`/app/results/${response.data.id}`, { state: { report: response.data } });
    } catch (error) {
      console.error('Analysis error:', error);
      
      if (error.response?.data?.detail?.error === 'limit_exceeded') {
        toast.error(error.response.data.detail.message);
        navigate('/pricing');
      } else {
        const errorMessage = error.response?.data?.detail || "Analysis failed. Please try again.";
        toast.error(typeof errorMessage === 'string' ? errorMessage : 'Analysis failed');
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const remainingAnalyses = usage ? usage.analyses_limit - usage.analyses_used : null;
  const usagePercent = usage ? (usage.analyses_used / usage.analyses_limit) * 100 : 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="analyze-page">
      <Navbar />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-heading font-bold text-2xl text-slate-900 mb-2">
            Analyze Your Resume
          </h1>
          <p className="text-slate-600">
            Upload your resume and paste the job description to get an AI-powered analysis.
          </p>
        </div>

        {/* Usage Indicator */}
        {usage && (
          <Card className={`mb-6 ${remainingAnalyses === 0 ? 'border-orange-300 bg-orange-50' : 'border-slate-200 bg-white'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {remainingAnalyses === 0 ? (
                    <AlertCircle className="w-5 h-5 text-orange-600" />
                  ) : (
                    <FileText className="w-5 h-5 text-slate-600" />
                  )}
                  <span className="text-sm font-medium text-slate-700">
                    {remainingAnalyses === 0 
                      ? 'No analyses remaining' 
                      : `${remainingAnalyses} ${remainingAnalyses === 1 ? 'analysis' : 'analyses'} remaining this month`
                    }
                  </span>
                </div>
                <span className="text-sm text-slate-500">
                  {usage.analyses_used} / {usage.analyses_limit} used
                </span>
              </div>
              <Progress value={usagePercent} className="h-2" />
              {remainingAnalyses === 0 && (
                <Button 
                  onClick={() => navigate('/pricing')} 
                  className="mt-3 bg-orange-600 hover:bg-orange-700 text-white"
                  size="sm"
                >
                  Upgrade for more analyses
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        <div className="space-y-6">
          {/* Resume Upload Card */}
          <Card className="bg-white border border-slate-200 shadow-[0_2px_8px_rgba(15,23,42,0.08)]">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-slate-700" />
                </div>
                <div>
                  <h2 className="font-heading font-semibold text-lg text-slate-900">Upload Resume</h2>
                  <p className="text-sm text-slate-500">PDF, DOCX, or TXT (Max 10MB)</p>
                </div>
              </div>

              <div
                data-testid="resume-upload-zone"
                className={`upload-zone cursor-pointer ${isDragActive ? 'drag-active' : ''} ${resumeFile ? 'border-lime-400 bg-lime-50/50' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.txt"
                  onChange={handleFileInputChange}
                  data-testid="resume-file-input"
                />
                
                <div className="text-center">
                  {resumeFile ? (
                    <div className="space-y-2">
                      <div className="w-12 h-12 mx-auto bg-lime-100 rounded-full flex items-center justify-center">
                        <CheckCircle2 className="w-6 h-6 text-lime-600" />
                      </div>
                      <p className="font-medium text-slate-900">{resumeFile.name}</p>
                      <p className="text-sm text-slate-500">Click or drag to replace</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="w-12 h-12 mx-auto bg-slate-100 rounded-full flex items-center justify-center">
                        <Upload className="w-6 h-6 text-slate-500" />
                      </div>
                      <p className="font-medium text-slate-700">
                        Drop your resume here or <span className="text-slate-900 underline">browse</span>
                      </p>
                      <p className="text-sm text-slate-500">Supports PDF, DOCX, TXT</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Job Description Card */}
          <Card className="bg-white border border-slate-200 shadow-[0_2px_8px_rgba(15,23,42,0.08)]">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-slate-700" />
                </div>
                <div>
                  <h2 className="font-heading font-semibold text-lg text-slate-900">Job Description</h2>
                  <p className="text-sm text-slate-500">Paste the job posting you're applying to</p>
                </div>
              </div>

              <Textarea
                data-testid="job-description-input"
                placeholder="Paste the full job description here...&#10;&#10;Include: Job title, responsibilities, requirements, qualifications, skills needed, etc."
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                className="min-h-[200px] bg-white border-slate-200 focus:ring-2 focus:ring-slate-900 focus:border-transparent resize-none font-body"
              />
              
              <p className="text-sm text-slate-500 mt-2">
                {jobDescription.length} characters
                {jobDescription.length < 20 && jobDescription.length > 0 && (
                  <span className="text-orange-500"> (minimum 20 required)</span>
                )}
              </p>
            </CardContent>
          </Card>

          {/* Analyze Button */}
          <Button
            data-testid="analyze-button"
            onClick={handleAnalyze}
            disabled={isAnalyzing || !resumeFile || jobDescription.trim().length < 20 || (usage && usage.analyses_used >= usage.analyses_limit)}
            className="w-full h-14 bg-slate-900 text-white hover:bg-slate-800 hover:shadow-lg transition-all duration-200 font-heading font-semibold text-lg rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isAnalyzing ? (
              <span className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin" />
                Analyzing your resume...
              </span>
            ) : (
              <span className="flex items-center gap-3">
                Analyze Match
                <ArrowRight className="w-5 h-5" />
              </span>
            )}
          </Button>

          {isAnalyzing && (
            <p className="text-center text-sm text-slate-500">
              This may take up to 30 seconds...
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
